<template>
  <b-container fluid>
    <b-row class="mb-2">
      <b-col class="px-sm-3 px-0">
        <h1>Orders</h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="px-sm-3 px-0">
        <b-overlay :show="loading" no-wrap fixed />
        <b-form inline class="mb-2" @submit.prevent="applyFilters">
          <b-form-input
            v-model="queryFilters.search"
            placeholder="Search by name, phone, number, email or network"
            title="Search by name, phone, number, email or network"
            class="mr-2 mb-2 single-input"
          />
          <b-form-group class="custom-search-input mr-2 mb-2">
            <multiselect
              v-model="queryFilters.status"
              select-label=""
              open-direction="bottom"
              :options="statusOptions"
              track-by="value"
              deselect-label="remove"
              :searchable="false"
              :close-on-select="true"
              :show-no-results="false"
              :hide-selected="false"
              label="text"
              placeholder="Filter by status"
              class="single-input"
            />
          </b-form-group>
          <custom-search-field
            v-if="hubCount > 1"
            v-model="queryFilters.hub"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by hub'"
            :path-to-search="'facilities'"
          />
          <custom-search-field
            v-model="queryFilters.network"
            class="mr-2 mb-2 single-input"
            :placeholder="'Filter by network'"
            :path-to-search="'networks'"
          />
          <custom-date-picker
            :initial-range="queryFilters.range"
            class="mr-2"
            @rangeChange="onRangeChange"
          />
          <b-button type="submit" variant="primary" class="mr-2 mb-2"
            >Filter</b-button
          >
          <b-button variant="secondary" class="mr-2 mb-2" @click="exportOrders"
            >Export</b-button
          >
        </b-form>
      </b-col>
    </b-row>
    <b-row class="overflow-auto">
      <b-col class="px-sm-3 px-0">
        <b-table
          hover
          no-local-sorting
          small
          :fields="fields"
          :items="data"
          :busy="busy"
          @sort-changed="onSortingChanged"
          @row-clicked="onRowClicked"
        >
          <template #cell(number)="row">
            <router-link :to="`/orders/${row.item.id}`">
              {{ row.item.number }}
            </router-link>
          </template>
          <template #cell(status)="row">
            {{ row.item.status | statusFormat }}
          </template>
          <template #cell(fulfillment_time)="row">
            {{ row.item.fulfillment_time | formatDateTime }}
          </template>
          <template #cell(network)="row">
            {{ row.item.network.name }}
          </template>
          <template #cell(user)="row">
            {{ row.item.user.first_name }}
            {{ row.item.user.last_name }}
          </template>
          <template #cell(address)="row">
            {{ row.item.address | formatAddressStreet }}
          </template>
          <template #cell(order_total)="row">
            {{ (row.item.order_total / 100) | currency }}
          </template>
          <template #cell(facility)="row">
            <template v-if="row.item.facility">
              {{ row.item.facility.name }}
            </template>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <pagination-handler v-if="data.length" :query-data="queryFilters" />
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import tableMixin from '../../mixins/TableMixin';
import ResourceAPI from '../../services/ResourceAPI';

export default {
  name: 'Orders',
  mixins: [tableMixin],
  data() {
    return {
      fields: [
        {
          key: 'number',
          label: 'Order ID',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'fulfillment_time',
          label: 'Fulfillment Time',
          sortable: true,
        },
        {
          key: 'address',
          label: 'Address',
          sortable: false,
        },
        {
          key: 'network',
          label: 'Network',
          sortable: true,
        },
        {
          key: 'user',
          label: 'Customer',
          sortable: true,
        },
        {
          key: 'order_total',
          label: 'Customer Total',
          sortable: true,
        },
        {
          key: 'facility',
          label: 'Hub',
          sortable: true,
        },
      ],
      data: [],
      loading: false,
      exportAlert: false,
      rowRouteName: 'order',
      statusOptions: [
        {
          value: 'placed',
          text: 'Placed',
        },
        {
          value: 'confirmed',
          text: 'Confirmed',
        },
        {
          value: 'preparing',
          text: 'Preparing',
        },
        {
          value: 'prepared',
          text: 'Prepared',
        },
        {
          value: 'enroute_expo',
          text: 'En Route Expo',
        },
        {
          value: 'compiling',
          text: 'Compiling',
        },
        {
          value: 'enroute_delivery_expo',
          text: 'En Route Delivery Expo',
        },
        {
          value: 'delivery_compiling',
          text: 'Delivery Compiling',
        },
        {
          value: 'enroute_dispatch',
          text: 'Enroute Dispatch',
        },
        {
          value: 'ready',
          text: 'Ready',
        },
        {
          value: 'enroute_customer',
          text: 'Enroute',
        },
        {
          value: 'completed',
          text: 'Completed',
        },
        {
          value: 'canceled',
          text: 'Canceled',
        },
        {
          value: 'refunded',
          text: 'Refunded',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['hubCount']),
  },
  watch: {
    hubCount(val) {
      if (val === 1) {
        this.removeTableColumn('hub');
      }
    },
  },
  methods: {
    onRangeChange(range) {
      this.queryFilters.range = {
        ...range,
      };
    },
    loadResults() {
      this.setRequestData();
      // Format search params for query
      const searchParams = { ...this.queryFilters };

      searchParams.status = searchParams.status
        ? searchParams.status.value
        : null;
      searchParams.fulfillmentCenter = searchParams.kitchen
        ? searchParams.kitchen.id
        : null;
      searchParams.network = searchParams.network
        ? searchParams.network.id
        : null;
      searchParams.facility = searchParams.hub ? searchParams.hub.id : null;
      searchParams.with = 'address,facility,network,user';
      searchParams.startDate =
        searchParams.range && 'start' in searchParams.range
          ? this.formatDateFilter(searchParams.range.start)
          : null;
      searchParams.endDate =
        searchParams.range && 'end' in searchParams.range
          ? this.formatDateFilter(searchParams.range.end)
          : null;
      searchParams.range = null;
      searchParams.hub = null;

      return ResourceAPI.getRecords('network-orders', searchParams)
        .then((data) => {
          this.setResponseData(data);
        })
        .catch((error) => alert(error));
    },
    exportOrders() {
      const me = this;

      me.loading = true;

      ResourceAPI.downloadCSV(
        'reports/customer-orders',
        {
          search: me.queryFilters.search,
          facility: me.queryFilters.hub ? me.queryFilters.hub.id : null,
          start:
            me.queryFilters.range && 'start' in me.queryFilters.range
              ? this.formatDateFilter(me.queryFilters.range.start)
              : null,
          end:
            me.queryFilters.range && 'end' in me.queryFilters.range
              ? this.formatDateFilter(me.queryFilters.range.end)
              : null,
          network: me.queryFilters.network ? me.queryFilters.network.id : null,
          status: me.queryFilters.status ? me.queryFilters.status.value : null,
        },
        'orders.csv'
      )
        .then(() => {
          me.loading = false;
        })
        .catch((error) => alert(error));
    },
  },
};
</script>
